import { Injectable, OnDestroy } from '@angular/core';

import { catchError, map, Observable, of, Subject, Subscription, tap, forkJoin } from 'rxjs';
import { BaseAbstractApi } from '../../../api/genericApi/base-abstract.api';
import { ResourceActionTypes } from '../enums/resource-actions.enum';
import { BaseModel } from '../models/base.model';

export interface ResourceAction {
    actionType: ResourceActionTypes;
    resource?: BaseModel<any, any, any>;
    error?: any;
    /** Re-emits the current resource into the currentResourceSubject stream on action completed */
    refreshResource?: boolean;
    /** Emits null into the currentResourceSubject stream on action completed */
    resetResource?: boolean;
    [key: string]: any;
}

export enum GenericErrorCodes {
    bulkPartialFail = 'bulkPartialFail'
}

// F => Frontend model
// B => backend api model
@Injectable()
export abstract class ResourceAbstractService<F extends BaseModel<F, B, IdType>, B, IdType = number> implements OnDestroy {
    protected actionCompletedSubject = new Subject<ResourceAction>();
    protected subscriptions: Subscription[] = [];
    public actionCompleted$ = this.actionCompletedSubject.asObservable();

    constructor() {
        this.subscriptions.push(
            this.actionCompleted$.subscribe(res => {
                this.messageCenter(res);
            }),
        );
    }

    abstract getApiService(): BaseAbstractApi<B, IdType>;

    /**
     * Construct the F/E model based on api model, after this method, F/E should not change F/E model values unless users make changes through UI
     * @param apiModel
     */
    abstract constructResourceModel(apiModel: B): F;
    abstract messageCenter(actionResult: ResourceAction): void;

    getById(id: IdType): Observable<F> {
        return this.getApiService().getById(id).pipe(
            map(x => this.constructResourceModel(x))
        );
    };

    createResource(
        resource: F,
        action: ResourceAction = {actionType: ResourceActionTypes.CREATE},
        broadcast: boolean = true,
    ): Observable<IdType> {
        action.resource = resource;
        return this.createResourceFromApiModel(resource, action, broadcast, resource.toApiModel());
    };

    createResourceFromApiModel(
        resource: F,
        action: ResourceAction = {actionType: ResourceActionTypes.CREATE},
        broadcast: boolean = true,
        apiModel: B
    ): Observable<IdType> {
        action.resource = resource;
        return this.getApiService().create(apiModel).pipe(
            tap(id => {
                resource.id = id;
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err,
                });
                throw new Error(err);
            })
        );
    }



    updateResource(
        resource: F,
        action: ResourceAction = {actionType: ResourceActionTypes.SAVE}, // @TODO - should this be changed to UPDATE?
        broadcast: boolean = true,
    ): Observable<IdType> {
        action.resource = resource;
        return this.updateResourceFromApiModel(resource, action, broadcast, resource.toApiModel());
    };

    updateResourceFromApiModel(
        resource: F,
        action: ResourceAction = {actionType: ResourceActionTypes.SAVE},
        broadcast: boolean = true,
        apiModel: B
    ): Observable<IdType> {
        action.resource = resource;

        return this.getApiService().update(apiModel).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err,
                });
                throw new Error(err);
            })
        );
    }


    //#region CRUD method's that return full models

    createFull(
        resource: F,
        action: ResourceAction = {actionType: ResourceActionTypes.CREATE},
        broadcast: boolean = true,
    ): Observable<B> {
        action.resource = resource;
        return this.getApiService().createFull(resource.toApiModel()).pipe(
            tap(id => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err,
                });
                throw new Error(err);
            })
        );
    };

    updateFull(
        resource: F,
        action: ResourceAction = {actionType: ResourceActionTypes.UPDATE},
        broadcast: boolean = true,
    ): Observable<B> {
        action.resource = resource;
        return this.getApiService().updateFull(resource.toApiModel()).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err,
                });
                throw new Error(err);
            })
        );
    };


    //#endregion

    /**
     * @param action.actionType is ResourceActionTypes.BULK_VISIBILITY
     */
    updateBulkResources(
        resources: F[],
        action: ResourceAction = {actionType: ResourceActionTypes.BULK_VISIBILITY},
        broadcast: boolean = true
    ): Observable<IdType[]> {
        action.count = resources.length; // @TODO account for failed calls

        const updateRequests: Observable<IdType>[] = resources.map(resource => {
            return this.getApiService().update(resource.toApiModel());
        });

        return forkJoin(updateRequests).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err,
                });
                throw new Error(err);
            })
        );
    }

    deleteResource(
        id: IdType,
        action: ResourceAction = {actionType: ResourceActionTypes.DELETE},
        broadcast: boolean = true
    ): Observable<IdType> {
        return this.getApiService().deleteById(id).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err
                });
                throw new Error(err);
            })
        );
    };

    /**
     * Currently leverages deleteById() method for each resource
     * @param resources - array of f/e models
     * @TODO currently has limitations handling individual resource errors, doesn't re-throw errors
    */
    bulkDeleteResource(
        resources: F[],
        action: ResourceAction = {actionType: ResourceActionTypes.BULK_DELETE},
        broadcast: boolean = true
    ): Observable<IdType[]> {

        const observables = resources.map(resource => {
            return this.getApiService().deleteById(resource.id).pipe(
                catchError((err) => {
                    action.error.code = GenericErrorCodes.bulkPartialFail;
                    action.error.failedIds.push(resource.id);

                    return of(null);
                })
            );
        });

        return forkJoin(observables).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err
                });
                return of(null);
            })
        );
    }

    /** @TODO currently has limitations handling individual resource errors, doesn't re-throw errors */
    bulkDeleteResourceByIds(
        resourceIds: IdType[],
        action: ResourceAction = {actionType: ResourceActionTypes.BULK_DELETE},
        broadcast: boolean = true
    ): Observable<IdType[]> {
        const observables = resourceIds.map(id => {
            return this.getApiService().deleteById(id).pipe(
                catchError((err) => {
                    action.error.code = GenericErrorCodes.bulkPartialFail;
                    action.error.failedIds.push(id);

                    return of(null);
                })
            );
        });

        return forkJoin(observables).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err
                });
                return of(null);
            })
        );
    }

    createBulk(
        resources: F[],
        action: ResourceAction = {actionType: ResourceActionTypes.DELETE},
        broadcast: boolean = true
    ): Observable<IdType[]> {
        return this.getApiService().createBatch(
            resources.map(x => x.toApiModel())
        ).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err
                });
                throw new Error(err);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }
}
